import React, { ChangeEvent } from 'react';
import {
	Box,
	Checkbox,
	FormControlLabel,
	Grid,
	Tooltip,
	Typography,
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { PercentSlider } from '@Components/PercentSlider';
import { Category as CategoryType } from '@Types';
import { useGlobalization } from '@Translations/useGlobalization';
import { stripCategorySuffix } from '@Utils/stripCategorySuffix';
import { CATEGORY_ACCENT } from './categoryColors';

interface CategoryHeaderProps {
	category?: CategoryType;
	forLoF: boolean;
	isLoading: boolean;
	isWeightingInvalid: boolean;
	onCategoryWeightingChanged?: (value: number) => void;
	onUseExclusivelyChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

export const CategoryHeader = ({
	category,
	forLoF,
	isLoading,
	isWeightingInvalid,
	onCategoryWeightingChanged,
	onUseExclusivelyChange,
}: CategoryHeaderProps) => {
	const { t } = useGlobalization();

	let displayName: string;
	if (category) {
		displayName = stripCategorySuffix(category.name);
	} else if (forLoF) {
		displayName = t('Category');
	} else {
		displayName = t('Components');
	}

	return (
		<Grid container className={isLoading ? 'wait' : ''}>
			<Grid item xs={12} md={5} zeroMinWidth sx={{ ml: -6 }}>
				<Typography
					sx={{
						fontSize: 11,
						fontWeight: 700,
						letterSpacing: '0.06em',
						textTransform: 'uppercase',
						color: '#97a0ac',
						mb: 0.25,
					}}>
					{t('Category')}
				</Typography>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<Typography
						data-testid="category-name"
						sx={{ fontWeight: 700, fontSize: 17 }}>
						{displayName}
					</Typography>
					{category?.isCondition && (
						<Tooltip
							arrow
							title={t(
								'Condition is a built-in category for inspection results. Add Observation-type components here (e.g. CCTV grades) — the factors that set a pipe’s condition once it has been inspected.',
							)}>
							<InfoOutlined
								sx={{
									fontSize: 16,
									color: '#b3bac4',
									cursor: 'default',
								}}
							/>
						</Tooltip>
					)}
				</Box>
				{category?.isCondition && (
					<FormControlLabel
						sx={{ ml: 0, mt: 0.5 }}
						control={
							<Checkbox
								size="small"
								sx={{ p: 0, mr: 1 }}
								checked={category?.useThisOnly}
								onChange={onUseExclusivelyChange}
								inputProps={{
									'aria-label': t('use condition only'),
								}}
							/>
						}
						label={
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: 0.5,
								}}>
								<Typography
									variant="body2"
									sx={{ whiteSpace: 'nowrap' }}>
									{t(
										'When a pipe has been inspected, use only the condition score',
									)}
								</Typography>
								<Tooltip
									title={t(
										'When on, an inspected pipe is scored only on its condition — general LoF components are ignored for that pipe.',
									)}>
									<InfoOutlined
										sx={{ fontSize: 15, color: '#b3bac4' }}
									/>
								</Tooltip>
							</Box>
						}
					/>
				)}
			</Grid>
			<Grid
				item
				xs={12}
				md
				sx={{
					pt: 0.5,
					pr: '40px',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'flex-end',
				}}>
				{!isLoading && (
					<Box sx={{ width: '100%', maxWidth: 480 }}>
						<Typography
							sx={{
								fontSize: 13,
								color: 'text.secondary',
								mb: 0.25,
							}}>
							{t('Category weight (% of total {{axis}})', {
								axis: forLoF ? 'LoF' : 'CoF',
							})}
						</Typography>
						<PercentSlider
							value={category ? category.weighting : 0}
							onSliderChanged={onCategoryWeightingChanged}
							disabled={
								category?.isCondition && category.useThisOnly
							}
							invalid={
								(!category?.isCondition &&
									isWeightingInvalid) ||
								(category?.isCondition &&
									!category.useThisOnly &&
									isWeightingInvalid)
							}
							marks="off"
							valueLabelDisplay="off"
							redesigned
							accent={CATEGORY_ACCENT}
							maxWidth={480}
						/>
					</Box>
				)}
			</Grid>
		</Grid>
	);
};
